import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { CustomerType } from '../types/customer';
import { useCustomer } from './CustomerContext';
import { logger } from '../services/logger';

interface CustomerFilterContextType {
  selectedCustomers: string[];
  setSelectedCustomers: (customerIds: string[]) => void;
  selectedTypes: CustomerType[];
  setSelectedTypes: (types: CustomerType[]) => void;
  isFilterActive: boolean;
  clearFilter: () => void;
}

const CustomerFilterContext = createContext<CustomerFilterContextType | undefined>(undefined);

interface CustomerFilterProviderProps {
  children: ReactNode;
}

export const CustomerFilterProvider: React.FC<CustomerFilterProviderProps> = ({ children }) => {
  const [selectedCustomers, setSelectedCustomers] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<CustomerType[]>(['condominio', 'empresa']);
  const { selectedCustomer } = useCustomer();

  // Sync with CustomerContext on mount and when selectedCustomer changes
  useEffect(() => {
    if (selectedCustomer) {
      setSelectedCustomers([selectedCustomer.id]);
      logger.debug('customer.filter.context', 'Synced with CustomerContext', {
        customerName: selectedCustomer.name
      });
    } else {
      setSelectedCustomers([]);
      logger.debug('customer.filter.context', 'Synced with CustomerContext: All clients');
    }
  }, [selectedCustomer]);

  const isFilterActive = selectedCustomers.length > 0;

  const clearFilter = () => {
    setSelectedCustomers([]);
    setSelectedTypes(['condominio', 'empresa']);
  };

  const value: CustomerFilterContextType = {
    selectedCustomers,
    setSelectedCustomers,
    selectedTypes,
    setSelectedTypes,
    isFilterActive,
    clearFilter,
  };

  return (
    <CustomerFilterContext.Provider value={value}>
      {children}
    </CustomerFilterContext.Provider>
  );
};

export const useCustomerFilter = (): CustomerFilterContextType => {
  const context = useContext(CustomerFilterContext);
  if (context === undefined) {
    throw new Error('useCustomerFilter must be used within a CustomerFilterProvider');
  }
  return context;
};